import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { api, RSD } from "../../lib/api";
import { useOrderSocket } from "../../hooks/useOrderSocket";
import { useShift } from "../../hooks/useShift";
import ShiftControl from "../../components/shift/ShiftControl";
import { Phone, Clock, Check, X, Timer, PackageCheck, RefreshCw, Bell, Volume2, Loader2, ChevronDown, Printer, Gift } from "lucide-react";
import { toast } from "sonner";
import { enablePush } from "../../lib/push";
import { SourceBadge } from "../../components/SourceBadge";
import { NewCustomerBadge } from "../../components/NewCustomerBadge";
import { CoinPaymentBadge, CoinAmount, isCoinOrder } from "../../components/CoinPaymentBadge";
import { reprintOrder } from "../../lib/printSlip";
import { canPrintSlips } from "../../lib/deviceClass";
import { localHHMM } from "../../lib/restaurantClock";
import { BOARD_GROUPS, boardGroup, tabGroups, desktopGroups, groupFlex } from "../../lib/boardGroups";
import {
  orderDeadlineMs, orderPreparationStartMs, displayRemainingMinutes, displayLateMinutes,
  urgencyTone, TONE_BAR, prepProgressPct, isReminderEligible, reminderKey,
  hasReminderFired, markReminderFired, reminderChannel, PREP_ACTIVE_STATUSES,
  needsEarlyReadyConfirm, remainingMsUntilDeadline, isScheduledVista, scheduledPrepStartMs,
} from "../../lib/prepTimer";
import { orderSourceKind, SOURCE_LABEL } from "../../lib/orderSource";
import { audioReady, claimAlarmSlot, playLateBeep, playNewOrderBell, playReminderBell,
  releaseAlarmSlot, unlockAudio } from "../../lib/kitchenAudio";

// Lifecycle precedence — used to ignore stale/out-of-order realtime events (never move an order backward).
const STATUS_RANK = { new: 0, time_change_pending: 0, accepted: 1, preparing: 1, ready: 2, completed: 3, cancelled: 3, rejected: 3 };
const rank = (s) => (s in STATUS_RANK ? STATUS_RANK[s] : 0);

function elapsed(iso) {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m <= 0 ? "sad" : `pre ${m} min`;
}

const RANGE_LABELS = { "10_15": "10–15 min", "15_20": "15–20 min", "20_30": "20–30 min", "30_PLUS": "30+ min" };

// Compact two-step operational selector: shows the current state, tap to pick a new one.
function OpsSelect({ testid, value, label, className, options, onPick, icon: Icon }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  return (
    <div className="relative">
      <button data-testid={testid} onClick={() => setOpen((o) => !o)}
        className={`text-sm font-bold px-3 py-2 rounded-lg flex items-center gap-1.5 ${className}`}>
        {Icon && <Icon size={15} />}{label}<ChevronDown size={14} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-40 bg-white rounded-xl border shadow-lg py-1 min-w-[190px]">
            {options.map(([v, text]) => (
              <button key={v} data-testid={`${testid}-opt-${v}`} onClick={() => { setOpen(false); if (v !== value) onPick(v); }}
                className={`w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 ${v === value ? "text-brand-red" : "text-slate-700"}`}>
                {text}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Minute-only kitchen countdown + real elapsed-time progress line (same canonical start/deadline
// pair that drives the 10/5 min reminders). Never shows seconds.
function PrepCountdown({ o, serverNow }) {
  const dl = orderDeadlineMs(o);
  if (dl == null) {
    return <div data-testid={`prep-timer-${o.order_number}`} className="mt-2 rounded-lg bg-slate-100 text-slate-500 text-center py-1.5 text-xs font-bold">Vreme nije dostupno</div>;
  }
  const remMs = dl - serverNow;
  const overdue = remMs <= 0;
  const tone = urgencyTone(remMs);
  const start = orderPreparationStartMs(o);
  const pct = overdue ? 100 : (prepProgressPct(start, dl, serverNow) ?? 100);
  const box = tone === "late" ? "bg-brand-red/10 text-brand-red"
    : tone === "red" ? "bg-brand-red/10 text-brand-red"
    : tone === "orange" ? "bg-brand-orange/10 text-brand-orange"
    : "bg-brand-green/10 text-brand-green";
  return (
    <div data-testid={`prep-timer-${o.order_number}`} className={`mt-2 rounded-lg text-center py-1.5 px-2 ${box}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{overdue ? "KASNI" : "PREOSTALO"}</div>
      <div data-testid={`prep-timer-value-${o.order_number}`} className="font-display font-black text-2xl tabular-nums leading-none">
        {overdue ? displayLateMinutes(-remMs) : displayRemainingMinutes(remMs)} min
      </div>
      <div data-testid={`prep-progress-${o.order_number}`} data-tone={tone} data-progress={Math.round(pct)}
        role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)}
        className="mt-2 h-1.5 rounded-full bg-black/10 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-700 ease-linear ${TONE_BAR[tone]}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const OFFSET_MIN = -10;
const OFFSET_MAX = 60;

function TimeChangeDialog({ order, onClose, onSubmit }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(10);   // canonical additional_minutes, 1-minute steps
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  React.useEffect(() => {
    let alive = true;
    api.get(`/orders/${order.id}/time-change-options`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [order.id]);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  // The server is the source of truth for which offsets are still valid (future + opening hours).
  const all = [...(data?.earlier || []), ...(data?.later || [])];
  const timeByDelta = Object.fromEntries(all.map((o) => [o.delta, o.time]));
  const empty = !loading && all.length === 0;
  const resultTime = timeByDelta[selected];
  const canSend = selected !== 0 && !!resultTime;

  const submit = async () => {
    if (!canSend || submitting) return;
    setSubmitting(true);
    try { await onSubmit(selected); } catch (_) { /* error toast already shown by onSubmit */ } finally { setSubmitting(false); }
  };
  const step = (d) => setSelected((v) => Math.min(OFFSET_MAX, Math.max(OFFSET_MIN, v + d)));
  const label = selected > 0 ? `+${selected} min` : `${selected} min`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={() => !submitting && onClose()} />
      <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm" role="dialog" aria-modal="true"
        aria-label="Predloži novo vreme" data-testid="time-change-dialog">
        <h3 className="font-display font-black text-lg mb-1">Predloži novo vreme</h3>
        <p className="text-sm text-slate-500 mb-4">
          Koliko dodatnog vremena ti je potrebno? Kupac je tražio <b>{data?.requested_time || order.pickup_time}</b> i ima 60s da prihvati.
        </p>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
        ) : empty ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="no-slots-msg">Nema dostupnih termina.</p>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <button data-testid="offset-minus" onClick={() => step(-1)} aria-label="Smanji za 1 minut"
              disabled={submitting || selected <= OFFSET_MIN}
              className="h-14 w-14 shrink-0 rounded-full border-2 border-slate-200 text-2xl font-black text-slate-700 transition-colors hover:border-brand-red/50 disabled:opacity-30">−</button>
            <div data-testid="offset-value" aria-live="polite"
              className="flex-1 text-center font-display text-3xl font-black tabular-nums text-slate-900">{label}</div>
            <button data-testid="offset-plus" onClick={() => step(1)} aria-label="Povećaj za 1 minut"
              disabled={submitting || selected >= OFFSET_MAX}
              className="h-14 w-14 shrink-0 rounded-full border-2 border-slate-200 text-2xl font-black text-slate-700 transition-colors hover:border-brand-red/50 disabled:opacity-30">+</button>
          </div>
        )}
        <p data-testid="resulting-time" className="text-sm mt-4 min-h-[20px] text-center text-slate-500">
          {selected === 0 ? "Izaberite dodatno vreme."
            : resultTime ? <>Novo vreme: <b className="text-slate-800">{resultTime}</b></>
            : "Restoran se zatvara pre tog termina."}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={onClose} disabled={submitting} className="rounded-lg py-2.5 bg-slate-100 font-semibold disabled:opacity-50">Otkaži</button>
          <button data-testid="submit-new-time" disabled={!canSend || submitting} onClick={submit}
            className="rounded-lg py-2.5 bg-brand-red hover:bg-brand-redHover text-white font-semibold flex items-center justify-center gap-1 disabled:opacity-50">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Slanje...</> : "Pošalji"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmRejectDialog({ order, onCancel, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const confirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try { await onConfirm(order); } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="confirm-reject-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={submitting ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm">
        <h3 className="font-display font-black text-lg mb-1">Odbiti porudžbinu?</h3>
        <p className="text-sm text-slate-500 mb-4">Da li ste sigurni da želite da odbijete ovu porudžbinu <b>#{order.public_code || order.order_number}</b>?</p>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="confirm-reject-cancel" onClick={onCancel} disabled={submitting}
            className="rounded-lg py-2.5 bg-slate-100 font-semibold disabled:opacity-50">Otkaži</button>
          <button data-testid="confirm-reject-yes" onClick={confirm} disabled={submitting}
            className="rounded-lg py-2.5 bg-brand-red hover:bg-brand-redHover text-white font-bold flex items-center justify-center gap-1.5 disabled:opacity-60">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Odbijanje...</> : "Da, odbij"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Early-READY guard: a manual PREPARING → READY far before the pickup deadline must be confirmed.
// The threshold decision happens in the caller from exact canonical timestamps; the minutes shown
// here are informational only.
function ConfirmEarlyReadyDialog({ order, remainingMs, onCancel, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, submitting]);
  const confirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try { await onConfirm(order); } finally { setSubmitting(false); }
  };
  const unknown = remainingMs == null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="confirm-early-ready-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={submitting ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm">
        <h3 className="font-display font-black text-lg mb-1">
          {unknown ? "Potvrda da je porudžbina spremna" : "Porudžbina je spremna ranije?"}
        </h3>
        <p className="text-sm text-slate-500 mb-4" data-testid="early-ready-body">
          {unknown
            ? "Nije moguće pouzdano utvrditi preostalo vreme za ovu porudžbinu. Da li ste sigurni da želite da je označite kao spremnu?"
            : `Do preuzimanja je ostalo još ${displayRemainingMinutes(remainingMs)} min. Da li ste sigurni da želite da označite porudžbinu kao spremnu?`}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="early-ready-cancel" onClick={onCancel} disabled={submitting}
            className="rounded-lg py-2.5 bg-slate-100 font-semibold disabled:opacity-50">OTKAŽI</button>
          <button data-testid="early-ready-confirm" onClick={confirm} disabled={submitting}
            className="rounded-lg py-2.5 bg-brand-green text-white font-bold flex items-center justify-center gap-1.5 disabled:opacity-60">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Čuvanje...</> : "DA, SPREMNA JE"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Restaurant-side cancellation of an already accepted (PREPARING) order. Reason codes are canonical;
// the Serbian labels are presentation only. "Drugo" requires a short internal note (never shown to
// the customer). Nothing is sent to the backend until the explicit final confirmation.
const CANCEL_REASONS = {
  local: [["CUSTOMER_CHANGED_MIND", "Kupac odustao"], ["ORDER_ENTRY_ERROR", "Greška pri unosu porudžbine"], ["OTHER", "Drugo"]],
  online: [["CUSTOMER_REQUESTED_CANCELLATION", "Kupac otkazao telefonom"], ["OTHER", "Drugo"]],
};

function ConfirmCancelDialog({ order, onCancel, onConfirm }) {
  const kind = orderSourceKind(order);
  const reasons = CANCEL_REASONS[kind === "local" ? "local" : "online"];
  const [reason, setReason] = useState(reasons[0][0]);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, submitting]);
  const confirm = async () => {
    if (submitting) return;
    if (reason === "OTHER" && !note.trim()) { setErr("Unesite razlog otkazivanja."); return; }
    setErr("");
    setSubmitting(true);
    try { await onConfirm(order, reason, note.trim()); } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="confirm-cancel-dialog">
      <div className="absolute inset-0 bg-black/50" onClick={submitting ? undefined : onCancel} />
      <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm">
        <h3 className="font-display font-black text-lg mb-1">Otkaži porudžbinu?</h3>
        <p className="text-sm text-slate-500 mb-3">
          Porudžbina je već prihvaćena i nalazi se u pripremi. Da li ste sigurni da želite da je otkažete?
        </p>
        <div className="space-y-1.5 mb-3">
          {reasons.map(([code, label]) => (
            <label key={code} data-testid={`cancel-reason-${code}`}
              className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-sm font-semibold cursor-pointer ${reason === code ? "border-brand-red bg-brand-red/5" : "border-slate-200"}`}>
              <input type="radio" name="cancel-reason" checked={reason === code} onChange={() => setReason(code)} />
              {label}
            </label>
          ))}
        </div>
        {reason === "OTHER" && (
          <textarea data-testid="cancel-note" value={note} maxLength={250} rows={2}
            onChange={(e) => setNote(e.target.value)} placeholder="Interna napomena (ne vidi je kupac)"
            className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand-red mb-2" />
        )}
        {err && <p data-testid="cancel-error" className="text-xs font-bold text-brand-red mb-2">{err}</p>}
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="cancel-dialog-back" onClick={onCancel} disabled={submitting}
            className="min-h-[48px] rounded-lg bg-slate-100 font-semibold disabled:opacity-50">NAZAD</button>
          <button data-testid="cancel-dialog-confirm" onClick={confirm} disabled={submitting}
            className="min-h-[48px] rounded-lg bg-brand-red hover:bg-brand-redHover text-white font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-60">
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Otkazivanje...</> : "POTVRDI OTKAZIVANJE"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ageLabel(iso, serverNow) {
  const m = Math.floor((serverNow - new Date(iso).getTime()) / 60000);
  return m < 1 ? "Čeka <1 min" : `Čeka ${m} min`;
}

// Large, touch-first primary action with built-in pending state (prevents accidental double taps).
function ActionBtn({ testid, onClick, className, icon: Icon, children }) {
  const [p, setP] = useState(false);
  return (
    <button data-testid={testid} disabled={p}
      onClick={async () => { setP(true); try { await onClick(); } finally { setP(false); } }}
      className={`w-full min-h-[52px] rounded-xl font-black text-base flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition-transform ${className}`}>
      {p ? <RefreshCw size={18} className="animate-spin" /> : <Icon size={18} />}{children}
    </button>
  );
}

function OrderItems({ o }) {
  const COLORS = { GREEN: "text-green-600", YELLOW: "text-yellow-600", RED: "text-red-600", BLUE: "text-blue-600", ORANGE: "text-orange-600", PURPLE: "text-purple-600", CYAN: "text-cyan-600", DEFAULT: "text-slate-500" };
  return (
    <div className="mt-2 space-y-1.5">
      {o.items.map((it, i) => (
        <div key={i}>
          <div className="text-[15px] font-bold text-slate-800 leading-snug flex flex-wrap items-center gap-1.5">
            <span>{it.quantity} × {it.name}</span>
            {it.person_label && <span data-testid="kitchen-person" className="inline-block bg-brand-red text-white text-xs font-black uppercase tracking-wide px-2 py-0.5 rounded-md">ZA: {it.person_label}</span>}
          </div>
          {it.modifiers?.length > 0 && (
            <ul className="pl-4 mt-0.5">
              {it.modifiers.map((m, j) => <li key={j} className={`text-xs list-disc list-inside font-semibold ${COLORS[m.color] || COLORS.DEFAULT}`}>{m.name}</li>)}
            </ul>
          )}
          {it.note && <div className="text-xs text-brand-orange pl-4"><span className="font-bold">Napomena:</span> <span className="italic">{it.note}</span></div>}
        </div>
      ))}
    </div>
  );
}

// Secondary, print-only icon action in the card's top-right square. One shared component used by
// every active card, both sources (PONESI.RS + LOKAL) and every restaurant.
// Disabled while a print attempt is in flight so a rapid double tap cannot start two jobs.
function ReprintBtn({ o }) {
  const [busy, setBusy] = useState(false);
  if (!canPrintSlips()) return null;      // printing is a desktop-only capability
  return (
    <button data-testid={`reprint-${o.order_number}`} disabled={busy}
      aria-label="Ponovo štampaj" title="Ponovo štampaj"
      onClick={async () => {
        if (busy) return;
        setBusy(true);
        try {
          await reprintOrder(api, o.id);
        } catch {
          toast.error("Štampanje nije uspelo. Pokušajte ponovo.");
        } finally {
          setBusy(false);
        }
      }}
      className="shrink-0 w-11 h-11 rounded-xl border border-slate-200 bg-white text-slate-400 flex items-center justify-center hover:text-slate-600 hover:border-slate-300 disabled:opacity-60 active:scale-95 transition-all">
      {busy ? <RefreshCw size={16} className="animate-spin" /> : <Printer size={16} />}
    </button>
  );
}

// Optional operational marker for a first-time customer of THIS restaurant. It never applies a
// discount and never changes the order price or status — it only records NOT_REWARDED / REWARDED.
const REWARD_NOTES = ["Besplatni pomfrit", "10% sledeća porudžbina", "Poklon desert"];

function RewardBtn({ o, onReward }) {
  const [open, setOpen] = useState(false);
  const [rec, setRec] = useState(o.new_customer_reward || null);
  const [busy, setBusy] = useState(false);
  const rewarded = rec?.reward_status === "REWARDED";
  const save = async (note) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurant/${o.restaurant_id}/new-customers/${o.user_id}/reward`,
        { reward_status: "REWARDED", reward_note: note || null });
      setRec(data);
      onReward?.(o.id, data);
      setOpen(false);
      toast.success("Kupac je označen kao nagrađen");
    } catch {
      toast.error("Nije uspelo. Pokušajte ponovo.");
    } finally { setBusy(false); }
  };
  if (rewarded) {
    return (
      <div data-testid={`rewarded-${o.order_number}`} className="mt-2 rounded-lg bg-brand-green/10 px-3 py-2 text-[11px] font-bold text-brand-green">
        Nagrađen{rec?.reward_note ? ` · ${rec.reward_note}` : ""}
      </div>
    );
  }
  return (
    <div className="mt-2">
      <button data-testid={`reward-${o.order_number}`} onClick={() => setOpen((v) => !v)}
        className="w-full rounded-lg py-2 text-[11px] font-black uppercase tracking-wider text-brand-green bg-brand-green/10 flex items-center justify-center gap-1">
        <Gift size={13} />Nagradi kupca
      </button>
      {open && (
        <div className="mt-2 grid gap-1" data-testid={`reward-options-${o.order_number}`}>
          {REWARD_NOTES.map((n) => (
            <button key={n} data-testid={`reward-note-${o.order_number}-${n.slice(0, 6)}`} disabled={busy}
              onClick={() => save(n)}
              className="rounded-lg py-2 text-[11px] font-bold bg-slate-100 text-slate-700 disabled:opacity-60">{n}</button>
          ))}
          <button data-testid={`reward-plain-${o.order_number}`} disabled={busy} onClick={() => save(null)}
            className="rounded-lg py-2 text-[11px] font-bold bg-slate-100 text-slate-500 disabled:opacity-60">Bez napomene</button>
        </div>
      )}
    </div>
  );
}

// The NOVI KUPAC flags are server-authoritative; if a realtime payload ever arrives without them
// (older event in flight), keep the previously known values so the badge never flickers away.
// Only these three fields are carried over — phone redaction and every other field stay as sent.
function keepNewCustomer(next, prev) {
  if (!prev || next?.new_customer_status !== undefined) return next;
  return { ...next, new_customer_status: prev.new_customer_status,
           is_first_ponesi_order_for_restaurant: prev.is_first_ponesi_order_for_restaurant,
           new_customer_reward: prev.new_customer_reward };
}

function OrderCard({ o, onAction, serverNow, onReward, accent }) {
  // Customer decision window: driven by the canonical server deadline (never restarted locally).
  const decisionLeft = o.time_change_deadline
    ? Math.max(0, Math.ceil((Date.parse(o.time_change_deadline) - serverNow) / 1000))
    : null;

  const isNew = o.status === "new";
  const isPending = o.status === "time_change_pending";
  const isPreparing = o.status === "preparing";
  const isReady = o.status === "ready";

  return (
    <div data-testid={`order-card-${o.order_number}`} data-accent={accent || ""}
      className={`bg-white rounded-2xl p-4 shadow-sm border transition-colors duration-500 ${
        accent === 5 ? "border-brand-red ring-2 ring-brand-red/40"
        : accent === 10 ? "border-brand-orange ring-2 ring-brand-orange/30"
        : "border-slate-200"}`}>
      {/* Header: BIG public order number + waiting age + top-right reprint square */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 gap-y-1 min-w-0 flex-wrap">
          <span className="font-display font-black text-3xl leading-none tracking-tight">{o.public_code || o.order_number}</span>
          <SourceBadge o={o} />
          <CoinPaymentBadge o={o} />
          <NewCustomerBadge o={o} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {(isNew || isPending) && (
            <span data-testid={`age-${o.order_number}`} className="text-xs font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-1 flex items-center gap-1"><Clock size={12} />{ageLabel(o.created_at, serverNow)}</span>
          )}
          {(isNew || isPending || isPreparing) && <ReprintBtn o={o} />}
        </div>
      </div>

      {/* Time urgency: NEW shows requested pickup; PREPARING shows countdown; READY shows ready label */}
      {isNew && (
        <div className="mt-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Preuzimanje</div>
          <div className="font-display font-black text-2xl text-brand-red leading-none">{o.pickup_time}{o.pickup_type === "asap" ? <span className="text-xs font-bold text-slate-400 ml-1">ASAP</span> : null}</div>
        </div>
      )}
      {isPreparing && isScheduledVista(o) && (
        <div data-testid={`scheduled-badge-${o.order_number}`} className="mt-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-1.5">
          <span className="text-[10px] font-black uppercase tracking-wider text-blue-700">Zakazano · {o.pickup_time}</span>
          {o.prep_start_at && (
            <span className="block text-[11px] font-bold text-blue-600">
              Početak pripreme: {localHHMM(o.prep_start_at)}
            </span>
          )}
        </div>
      )}
      {isPreparing && <PrepCountdown o={o} serverNow={serverNow} />}
      {isReady && (
        <div className="mt-2 rounded-lg bg-brand-green/10 text-brand-green text-center py-1.5 text-xs font-bold uppercase tracking-wider">Spremno za preuzimanje</div>
      )}

      {/* Food — the scannable core */}
      <OrderItems o={o} />

      {/* Important order-level note stands out */}
      {o.note && (
        <div className="mt-2 rounded-lg bg-brand-orange/10 px-3 py-2">
          <div className="text-[10px] font-black uppercase tracking-wider text-brand-orange">Napomena</div>
          <div className="text-sm font-bold text-brand-orange">{o.note}</div>
        </div>
      )}

      {/* Waiting-for-customer state (unconfirmed new time) — no kitchen countdown here.
          The backend owns the outcome (accept / cancel / ASAP auto-accept); the UI only displays it. */}
      {isPending && (
        <div data-testid={`pending-time-${o.order_number}`} className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
          <p className="text-[10px] font-black uppercase tracking-wider text-amber-700 flex items-center gap-1"><Timer size={12} /> ČEKA SE ODGOVOR KUPCA</p>
          <p className="text-sm font-bold text-slate-700 mt-1">Predloženo vreme: <span className="font-display font-black text-base text-amber-700">{o.proposed_time}</span></p>
          {decisionLeft != null && (
            <>
              <p data-testid={`pending-decision-${o.order_number}`} className="text-xs font-semibold text-slate-500 mt-0.5">
                {decisionLeft > 0 ? `Preostalo za odluku: ${decisionLeft} s` : "Potvrđuje se…"}
              </p>
              <div className="h-1.5 bg-amber-200 rounded-full mt-1.5 overflow-hidden"><div className="h-full bg-amber-500 rounded-full transition-[width] duration-700 ease-linear" style={{ width: `${(decisionLeft / 60) * 100}%` }} /></div>
            </>
          )}
        </div>
      )}

      {/* Secondary: customer + total (subordinate) */}
      <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
        <span className="truncate">{o.customer_name}{o.customer_phone ? <> · <a href={`tel:${o.customer_phone}`} className="text-brand-red font-semibold">{o.customer_phone}</a></> : null}</span>
        {isCoinOrder(o)
          ? <span className="font-display font-black text-amber-700 flex items-center gap-1"><CoinAmount o={o} size={15} /></span>
          : <span className="font-display font-black text-slate-700">{RSD(o.total)}</span>}
      </div>

      {/* Primary action — one obvious, large button per card */}
      <div className="mt-3">
        {isNew && (
          <>
            <ActionBtn testid={`accept-${o.order_number}`} icon={Check} onClick={() => onAction(o, "accept")} className="bg-brand-green text-white">PRIHVATI</ActionBtn>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button data-testid={`changetime-${o.order_number}`} onClick={() => onAction(o, "change")} className="rounded-lg py-2.5 text-xs font-bold text-slate-600 bg-slate-100 flex items-center justify-center gap-1"><Timer size={13} />Promeni vreme</button>
              <button data-testid={`reject-${o.order_number}`} onClick={() => onAction(o, "reject")} className="rounded-lg py-2.5 text-xs font-bold text-brand-red bg-brand-red/10 flex items-center justify-center gap-1"><X size={13} />Odbij</button>
            </div>
          </>
        )}
        {isPending && (
          <button data-testid={`reject-${o.order_number}`} onClick={() => onAction(o, "reject")} className="w-full rounded-lg py-2.5 text-xs font-bold text-brand-red bg-brand-red/10 flex items-center justify-center gap-1"><X size={13} />Odbij</button>
        )}
        {isPreparing && (
          <>
            <ActionBtn testid={`ready-${o.order_number}`} icon={PackageCheck} onClick={() => onAction(o, "ready")} className="bg-brand-green text-white">SPREMNO</ActionBtn>
            <button data-testid={`cancel-${o.order_number}`} onClick={() => onAction(o, "cancel")}
              className="mt-2 w-full min-h-[40px] rounded-lg text-xs font-bold text-slate-500 bg-slate-100 flex items-center justify-center gap-1">
              <X size={13} />Otkaži
            </button>
          </>
        )}
        {isReady && <ActionBtn testid={`complete-${o.order_number}`} icon={Check} onClick={() => onAction(o, "completed")} className="bg-slate-800 text-white">PREUZETO</ActionBtn>}
      </div>
      {o.new_customer_status === "NEW" && o.user_id && <RewardBtn o={o} onReward={onReward} />}
    </div>
  );
}

function ReadyShortcut({ o, onOpen, onComplete }) {
  const [p, setP] = useState(false);
  const kind = orderSourceKind(o);
  // Source tint is presentation only — never used for logic. READY keeps its green cue (dot).
  const tint = isCoinOrder(o) ? "bg-white border-amber-400 hover:border-amber-500"
    : kind === "online" ? "bg-sky-50 border-sky-200 hover:border-sky-400"
    : kind === "local" ? "bg-amber-50 border-amber-200 hover:border-amber-400"
    : "bg-white border-slate-200 hover:border-slate-400";
  const labelTone = kind === "online" ? "text-sky-700" : kind === "local" ? "text-amber-700" : "text-slate-400";
  return (
    <div role="button" tabIndex={0} data-testid={`ready-shortcut-${o.order_number}`} data-source-kind={kind} onClick={() => onOpen(o)}
      className={`rounded-xl border-2 p-1.5 cursor-pointer transition-colors aspect-square flex flex-col items-center justify-center gap-0.5 ${tint}`}>
      <div className="flex items-center gap-1 leading-none">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-green shrink-0" />
        <span className="font-display font-black text-xl leading-none tracking-tight truncate">{o.public_code || o.order_number}</span>
      </div>
      <div data-testid={`ready-source-${o.order_number}`} className={`text-[9px] font-black uppercase tracking-wider leading-none ${labelTone}`}>
        {SOURCE_LABEL[kind]}
      </div>
      {isCoinOrder(o) && (
        <div className="flex items-center justify-center gap-0.5 text-[10px] font-black text-amber-700 leading-none">
          <CoinAmount o={o} size={11} />
        </div>
      )}
      <button data-testid={`complete-${o.order_number}`} disabled={p}
        onClick={async (e) => { e.stopPropagation(); setP(true); try { await onComplete(o); } finally { setP(false); } }}
        className="w-full rounded-lg bg-slate-800 text-white font-black text-[10px] leading-none py-1.5 disabled:opacity-60 active:scale-95 transition-transform flex items-center justify-center gap-1">
        {p && <RefreshCw size={11} className="animate-spin" />}PREUZETO
      </button>
    </div>
  );
}

function ReadyStrip({ orders, onOpen, onComplete, className = "", scrollClassName = "" }) {
  return (
    <div className={className} data-testid="ready-strip">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="w-2 h-2 rounded-full bg-brand-green" />
        <h2 className="font-display font-black text-xs tracking-wide">SPREMNO</h2>
        <span className="text-xs font-black bg-white rounded-full px-1.5" data-testid="ready-count">{orders.length}</span>
      </div>
      <div className={`space-y-2 ${scrollClassName}`}>
        {orders.map((o) => <ReadyShortcut key={o.id} o={o} onOpen={onOpen} onComplete={onComplete} />)}
        {orders.length === 0 && <p className="text-[10px] text-slate-300 text-center py-4">Nema</p>}
      </div>
    </div>
  );
}

function OrderDetailModal({ o, onClose, onComplete }) {
  const [p, setP] = useState(false);
  const payLabel = isCoinOrder(o) ? "Ponesi Coins (nagrada)"
    : o.payment_method === "card" ? "Kartica u restoranu" : "Keš pri preuzimanju";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="order-detail-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between">
          <div>
            <span className="font-display font-black text-3xl leading-none">{o.public_code || o.order_number}</span>
            <SourceBadge o={o} className="ml-2" />
            <CoinPaymentBadge o={o} className="ml-2" />
            <NewCustomerBadge o={o} className="ml-2" />
            <span className="ml-2 text-[11px] font-black uppercase tracking-wider bg-brand-green/10 text-brand-green rounded-full px-2 py-1">Spremno</span>
          </div>
          <button onClick={onClose} className="bg-slate-100 rounded-full p-2" data-testid="close-detail"><X size={16} /></button>
        </div>
        <div className="mt-3 flex items-center justify-between text-sm"><span className="text-slate-500">Preuzimanje</span><span className="font-bold">{o.pickup_time}{o.pickup_type === "asap" ? " (ASAP)" : ""}</span></div>
        <div className="mt-1 flex items-center justify-between text-sm"><span className="text-slate-500">Plaćanje</span><span className="font-bold">{payLabel}</span></div>
        <OrderItems o={o} />
        {o.note && (
          <div className="mt-2 rounded-lg bg-brand-orange/10 px-3 py-2">
            <div className="text-[10px] font-black uppercase tracking-wider text-brand-orange">Napomena</div>
            <div className="text-sm font-bold text-brand-orange">{o.note}</div>
          </div>
        )}
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <span className="truncate">{o.customer_name}{o.customer_phone ? <> · <a href={`tel:${o.customer_phone}`} className="text-brand-red font-semibold">{o.customer_phone}</a></> : null}</span>
          {isCoinOrder(o)
            ? <span className="font-display font-black text-amber-700"><CoinAmount o={o} size={15} /></span>
            : <span className="font-display font-black text-slate-700">{RSD(o.total)}</span>}
        </div>
        <div className="mt-4">
          <button data-testid={`detail-complete-${o.order_number}`} disabled={p}
            onClick={async () => { setP(true); try { await onComplete(o); onClose(); } catch { setP(false); } }}
            className="w-full min-h-[52px] rounded-xl bg-slate-800 text-white font-black text-base flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.98] transition-transform">
            {p ? <RefreshCw size={18} className="animate-spin" /> : <Check size={18} />}PREUZETO
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OrdersBoard() {
  const { restaurant, role, features, reload } = useOutletContext();
  const rid = restaurant.id;
  const shiftState = useShift(rid);
  const canViewShiftHistory = ["OWNER", "MANAGER", "PLATFORM_ADMIN"].includes((role || "").toUpperCase());
  const [orders, setOrders] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [confirmReject, setConfirmReject] = useState(null);
  const [confirmEarlyReady, setConfirmEarlyReady] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [accepting, setAccepting] = useState(restaurant.accepting_orders !== false);
  const [tempClosed, setTempClosed] = useState(restaurant.temp_closed === true);
  const [range, setRange] = useState(restaurant.normal_pickup_range || "15_20");
  const [audioUnlocked, setAudioUnlocked] = useState(() => audioReady());
  const [tab, setTab] = useState("ponesi");
  const [peekPonesi, setPeekPonesi] = useState(false);
  const [detail, setDetail] = useState(null);
  const notified = useRef(new Set());
  const lateAlerted = useRef(new Set());
  const [accents, setAccents] = useState({});
  const accentTimers = useRef({});
  const lastReminderSoundAt = useRef(0);
  const lastLateSoundAt = useRef(0);
  const [nowTick, setNowTick] = useState(Date.now());
  const offsetRef = useRef(0);
  const fetchServerTime = async () => { try { const { data } = await api.get("/time"); offsetRef.current = new Date(data.now).getTime() - Date.now(); } catch {} };

  const didInit = useRef(false);

  // Ring/notify once per genuinely-new order (id-keyed, survives reconnect; resets on refresh).
  const maybeNotify = (list, silent) => {
    const newIds = list.filter((o) => o.status === "new" || o.status === "time_change_pending").map((o) => o.id);
    for (const id of newIds) {
      if (notified.current.has(id)) continue;
      notified.current.add(id);
      if (silent) continue;
      const o = list.find((x) => x.id === id);
      console.log("[orders] new order", id);
      toast(`Nova porudžbina #${o?.order_number ?? ""}`);
      if ("Notification" in window && Notification.permission === "granted") { try { new Notification("Ponesi", { body: `Nova porudžbina #${o?.order_number ?? ""}` }); } catch {} }
    }
  };

  // Authoritative reconcile (initial fetch, reconnect recovery, and 15s safety-net poll).
  // Merge by id; keep very-recent local realtime orders not yet in the snapshot (race window).
  const reconcile = async () => {
    try {
      const { data } = await api.get("/restaurant/orders?active=1");
      const scoped = data.filter((o) => o.restaurant_id === rid);
      setOrders((prev) => {
        const byId = new Map(scoped.map((o) => [o.id, o]));
        for (const p of prev) {
          // Never clobber an in-flight optimistic transition with a possibly-older snapshot.
          if (inflight.current.has(p.id)) { byId.set(p.id, p); continue; }
          if (!byId.has(p.id) && ["new", "time_change_pending", "preparing", "ready"].includes(p.status)) {
            const age = Date.now() - new Date(p.created_at || 0).getTime();
            if (age < 60000) byId.set(p.id, p);
          }
        }
        return Array.from(byId.values());
      });
      maybeNotify(scoped, !didInit.current); // seed silently on first load
      // On first load, mark already-overdue PREPARING orders as alerted so a page refresh never replays the late sound.
      if (!didInit.current) {
        const now = Date.now() + offsetRef.current;
        for (const o of scoped) {
          if (o.status === "preparing") { const dl = orderDeadlineMs(o); if (dl != null && now >= dl) lateAlerted.current.add(o.id); }
        }
      }
      didInit.current = true;
    } catch {}
  };

  useEffect(() => { didInit.current = false; notified.current = new Set(); lateAlerted.current = new Set(); reconcile(); setAccepting(restaurant.accepting_orders !== false); setTempClosed(restaurant.temp_closed === true); setRange(restaurant.normal_pickup_range || "15_20"); /* eslint-disable-next-line */ }, [rid]);
  useEffect(() => { if (typeof Notification !== "undefined" && Notification.permission === "granted") enablePush(); }, []);

  // One shared 1s clock for ALL preparing countdowns + server-time skew correction.
  // On tab-return we re-sync server time so a slept tablet shows the correct remaining time.
  useEffect(() => {
    fetchServerTime();
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    const onVis = () => { if (document.visibilityState === "visible") { fetchServerTime(); setNowTick(Date.now()); } };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Late-order alert: play ONE discreet sound when a PREPARING order first crosses its deadline.
  // Dedup via lateAlerted set (survives rerenders); already-late orders are seeded silently on load
  // (no replay on refresh). Batched/throttled so many simultaneous overdue orders don't overlap.
  useEffect(() => {
    const now = nowTick + offsetRef.current;
    const newly = orders.filter((o) => o.status === "preparing" && !lateAlerted.current.has(o.id))
      .filter((o) => { const dl = orderDeadlineMs(o); return dl != null && now >= dl; });
    if (newly.length === 0) return;
    newly.forEach((o) => lateAlerted.current.add(o.id));
    if (audioUnlocked && Date.now() - lastLateSoundAt.current > 2500) {
      lastLateSoundAt.current = Date.now();
      playLateBeep();
    }
    newly.forEach((o) => api.post(`/orders/${o.id}/mark-late`).catch(() => {}));
    // eslint-disable-next-line
  }, [nowTick, orders]);

  // Scheduled Vista orders: exactly TWO reminders, both from canonical server timestamps —
  // "start preparing" at prep_start_at and a final one 10 min before pickup. When prep <= 10 min
  // the two collapse into a single reminder (both keys are marked, so neither repeats).
  useEffect(() => {
    const now = nowTick + offsetRef.current;
    const dueStart = [], dueFinal = [], keysToMark = [];
    for (const o of orders) {
      if (!PREP_ACTIVE_STATUSES.includes(o.status) || !isScheduledVista(o)) continue;
      const dl = orderDeadlineMs(o);
      const startAt = scheduledPrepStartMs(o);
      if (dl == null || startAt == null) continue;
      const kStart = reminderKey(o.id, dl, "vista-start"), kFinal = reminderKey(o.id, dl, "vista-final10");
      const firedStart = hasReminderFired(kStart), firedFinal = hasReminderFired(kFinal);
      const within10 = dl - now <= 10 * 60000;
      if (now >= startAt && !firedStart) {
        keysToMark.push(kStart);
        // Too late to be useful (pickup already passed) → mark silently, no alarm backlog.
        if (dl - now > -60000) dueStart.push(o);
        // Prep start already inside the final window → that single reminder covers both.
        if (within10 && !firedFinal) keysToMark.push(kFinal);
      } else if (within10 && (dl - startAt) > 10 * 60000 && !firedFinal) {
        keysToMark.push(kFinal);
        if (dl - now > -60000) dueFinal.push(o);
      }
    }
    if (keysToMark.length === 0) return;
    markReminderFired(keysToMark);
    try { reminderChannel()?.postMessage({ keys: keysToMark }); } catch {}
    const hit = [...dueStart, ...dueFinal];
    if (hit.length === 0) return;
    const code = (o) => o.public_code || o.order_number;
    toast(dueStart.length
      ? (dueStart.length > 1
          ? `Vreme je da počnete pripremu ${dueStart.length} zakazane porudžbine`
          : `Vreme je da počnete pripremu porudžbine ${code(dueStart[0])}`)
      : (dueFinal.length > 1
          ? `${dueFinal.length} zakazane porudžbine: još 10 min do preuzimanja`
          : `Još 10 min do vremena preuzimanja · ${code(dueFinal[0])}`));
    if (audioUnlocked && Date.now() - lastReminderSoundAt.current > 2000) {
      lastReminderSoundAt.current = Date.now();
      playReminderBell(dueStart.length ? 2 : 1);
    }
    // eslint-disable-next-line
  }, [nowTick, orders, audioUnlocked]);

  // Long-order preparation reminders (10 min / 5 min remaining).
  // Server-authoritative timestamps, exactly-once per order+deadline+threshold (localStorage),
  // one coalesced sound + aggregate toast per tick, no backlog replay after sleep/refresh.
  useEffect(() => {
    const now = nowTick + offsetRef.current;
    const due10 = [], due5 = [], keysToMark = [];
    for (const o of orders) {
      if (!PREP_ACTIVE_STATUSES.includes(o.status)) continue;
      const dl = orderDeadlineMs(o);
      if (dl == null || !isReminderEligible(o)) continue;
      const remMs = dl - now;
      const k10 = reminderKey(o.id, dl, 10), k5 = reminderKey(o.id, dl, 5);
      const fired10 = hasReminderFired(k10), fired5 = hasReminderFired(k5);
      // Crossed 5 min → the 10 min reminder is no longer relevant (no missed-sound backlog).
      if (remMs <= 5 * 60000 && !fired10) keysToMark.push(k10);
      if (remMs <= 2 * 60000) {                       // too late to be useful → silence, mark only
        if (!fired5) keysToMark.push(k5);
        continue;
      }
      if (remMs <= 5 * 60000) { if (!fired5) { keysToMark.push(k5); due5.push(o); } continue; }
      if (remMs <= 10 * 60000) { if (!fired10) { keysToMark.push(k10); due10.push(o); } }
    }
    if (keysToMark.length === 0) return;
    markReminderFired(keysToMark);
    try { reminderChannel()?.postMessage({ keys: keysToMark }); } catch {}
    const hit = [...due5, ...due10];
    if (hit.length === 0) return;
    const level = due5.length > 0 ? 5 : 10;           // the more urgent reminder wins
    const label = level === 5 ? "5 MIN DO PREUZIMANJA" : "10 MIN DO PREUZIMANJA";
    const group = level === 5 ? due5 : due10;
    toast(group.length > 1
      ? `${group.length} porudžbine imaju još ${level} min`
      : `${label} · ${group[0].public_code || group[0].order_number}${group[0].order_source === "RESTAURANT_INTERNAL" ? " · LOKAL" : " · PONESI"}`);
    if (audioUnlocked && Date.now() - lastReminderSoundAt.current > 2000) {
      lastReminderSoundAt.current = Date.now();
      playReminderBell(level === 5 ? 2 : 1);
    }
    setAccents((prev) => {
      const next = { ...prev };
      for (const o of due10) next[o.id] = 10;
      for (const o of due5) next[o.id] = 5;
      return next;
    });
    for (const o of hit) {
      clearTimeout(accentTimers.current[o.id]);
      accentTimers.current[o.id] = setTimeout(() => {
        setAccents((prev) => { const n = { ...prev }; delete n[o.id]; return n; });
      }, 2500);
    }
    // eslint-disable-next-line
  }, [nowTick, orders, audioUnlocked]);

  // Canonical pending→confirmed feedback: the backend decides (explicit accept vs ASAP auto-accept),
  // the board only announces the result once per order.
  const prevStatusRef = useRef({});
  useEffect(() => {
    for (const o of orders) {
      const prev = prevStatusRef.current[o.id];
      if (prev === "time_change_pending" && ["preparing", "accepted"].includes(o.status)) {
        toast.success(o.time_change_resolution === "AUTO_ACCEPTED"
          ? "Novo vreme automatski prihvaćeno" : "Kupac je prihvatio novo vreme");
      }
      prevStatusRef.current[o.id] = o.status;
    }
  }, [orders]);

  useEffect(() => () => Object.values(accentTimers.current).forEach(clearTimeout), []);

  // Fallback safety net + multi-device status sync: reconcile every 15s while the tab is visible.
  // The same lifecycle also re-reads the canonical paid-service entitlements (shell `reload`), so a
  // PLATFORM_ADMIN VISTA change reaches the board without a logout — no extra polling loop.
  useEffect(() => {
    const sync = () => { if (document.visibilityState === "visible") { reconcile(); reload?.(); } };
    const t = setInterval(sync, 15000);
    const onVis = () => { if (document.visibilityState === "visible") reload?.(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line
  }, [rid]);

  // NEW-order sound is always enabled; only the real browser audio state can block it. One shared
  // recovery path for every lifecycle event (gesture, focus, visibility, PWA resume, bfcache) —
  // several of them fire together, so it must be idempotent.
  const restoreAudio = useCallback(async () => {
    const ok = await unlockAudio();
    setAudioUnlocked(ok);
    return ok;
  }, []);

  useEffect(() => {
    const onGesture = () => { if (!audioReady()) restoreAudio(); };
    const onResume = () => { if (document.visibilityState === "visible") restoreAudio(); };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("focus", onResume);
    window.addEventListener("pageshow", onResume);
    document.addEventListener("visibilitychange", onResume);
    restoreAudio();
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
      document.removeEventListener("visibilitychange", onResume);
    };
  }, [restoreAudio]);

  // Centralized NEW-order alarm: ONE loud bell every 3s while ≥1 order is in NEW state, else silent.
  // Keyed on the boolean hasNewOrders so the interval is created once (never per-order, never
  // duplicated). claimAlarmSlot keeps a second tab on the SAME device silent while this one rings.
  const hasNewOrders = orders.some((o) => o.status === "new");
  useEffect(() => {
    if (!hasNewOrders) { releaseAlarmSlot(rid); return; }
    const ring = () => {
      if (!audioReady()) { setAudioUnlocked(false); return; }   // suspended → show the unlock action
      if (!claimAlarmSlot(rid)) return;                          // another tab on this device rings
      if (!playNewOrderBell()) setAudioUnlocked(false);
    };
    ring();                                        // ring immediately (arrival, refresh, reconnect)
    const t = setInterval(ring, 3000);             // then every 3 seconds
    return () => { clearInterval(t); releaseAlarmSlot(rid); };
  }, [hasNewOrders, audioUnlocked, rid]);

  useOrderSocket((msg) => {
    if (msg.type === "settings_update" && msg.restaurant_id === rid) {
      if ("accepting_orders" in msg) setAccepting(msg.accepting_orders !== false);
      if ("temp_closed" in msg) setTempClosed(msg.temp_closed === true);
      if ("normal_pickup_range" in msg) setRange(msg.normal_pickup_range);
    }
    if (["order_update", "new_order"].includes(msg.type) && msg.order?.restaurant_id === rid) {
      if (msg.type === "new_order") maybeNotify([msg.order], false);
      setOrders((prev) => {
        const idx = prev.findIndex((o) => o.id === msg.order.id);
        if (idx >= 0) {
          // Ignore stale/out-of-order events that would move a card backward (multi-device safety).
          if (rank(msg.order.status) < rank(prev[idx].status)) return prev;
          const c = [...prev]; c[idx] = keepNewCustomer(msg.order, prev[idx]); return c;
        }
        return [msg.order, ...prev];
      });
    }
  }, { restaurantId: rid, onReady: () => { fetchServerTime(); reconcile(); } });

  // One operational endpoint for all three Orders-page controls; WS keeps other devices in sync.
  const setOps = async (patch, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      const { data } = await api.patch("/restaurant/operational", { restaurant_id: rid, ...patch });
      setAccepting(data.accepting_orders !== false);
      setTempClosed(data.temp_closed === true);
      setRange(data.normal_pickup_range);
      toast.success("Sačuvano");
    } catch { toast.error("Greška"); }
  };

  const inflight = useRef(new Set());
  // Optimistic transition: move the card instantly, confirm on the server, roll back safely on failure.
  const applyPatch = (id, patch) => setOrders((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const performTransition = async (o, type) => {
    if (inflight.current.has(o.id)) return;
    inflight.current.add(o.id);
    const snapshot = orders.find((x) => x.id === o.id);
    const nowIso = new Date().toISOString();
    const patch = type === "accept"
      ? { status: "preparing", accepted_at: nowIso, confirmed_pickup_time: o.confirmed_pickup_time || o.pickup_time, proposed_time: null, time_change_deadline: null }
      : type === "ready" ? { status: "ready", ready_at: nowIso, customer_phone: null }
      : type === "completed" ? { status: "completed", customer_phone: null }
      : type === "reject" ? { status: "rejected", customer_phone: null } : null;
    if (patch) applyPatch(o.id, patch);
    try {
      if (type === "accept") await api.post(`/orders/${o.id}/accept`);
      else if (type === "reject") await api.post(`/orders/${o.id}/reject`);
      else await api.post(`/orders/${o.id}/status`, { status: type });
    } catch (e) {
      if (snapshot) setOrders((prev) => prev.map((x) => (x.id === o.id ? snapshot : x)));
      const d = e?.response?.data?.detail;
      toast.error((d && (typeof d === "object" ? d.message : d)) || "Status porudžbine je već promenjen.");
      reconcile();
    } finally { inflight.current.delete(o.id); }
  };
  // Confirmed restaurant-side cancellation of an accepted order. Revalidates fresh canonical state
  // first; the backend compare-and-set is the final authority (a concurrent READY always wins).
  const cancelOrderNow = async (o, reason, note) => {
    if (inflight.current.has(o.id)) return;
    inflight.current.add(o.id);
    try {
      const { data } = await api.get(`/orders/${o.id}/staff-state`);
      if (data.status !== "preparing") {
        setConfirmCancel(null);
        toast.error("Status porudžbine je već promenjen.");
        reconcile();
        return;
      }
      await api.post(`/orders/${o.id}/restaurant-cancel`, { reason, internal_note: note || undefined });
      applyPatch(o.id, { status: "cancelled", customer_phone: null });
      setConfirmCancel(null);
      toast.success("Porudžbina je otkazana.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const resolved = (d && typeof d === "object" && d.code === "ORDER_ALREADY_RESOLVED");
      toast.error((d && typeof d === "object" && d.message) || "Porudžbina nije otkazana. Pokušajte ponovo.");
      if (resolved) setConfirmCancel(null);
      reconcile();
    } finally { inflight.current.delete(o.id); }
  };

  const action = (o, type) => {
    if (type === "change") { setDialog(o); return; }
    if (type === "reject") { setConfirmReject(o); return; }  // destructive → require confirmation
    if (type === "cancel") { setConfirmCancel(o); return; }  // accepted order → mandatory confirmation
    // Manual PREPARING → READY far before the deadline (or with no canonical deadline) → confirm.
    if (type === "ready" && o.status === "preparing") {
      const serverNow = Date.now() + offsetRef.current;
      if (needsEarlyReadyConfirm(o, serverNow)) {
        setConfirmEarlyReady({ id: o.id, remainingMs: remainingMsUntilDeadline(o, serverNow) });
        return;
      }
    }
    return performTransition(o, type);
  };

  // Confirmed early READY: revalidate the FRESH canonical state before pushing the transition.
  const confirmEarlyReadyNow = async () => {
    const id = confirmEarlyReady?.id;
    if (!id) return;
    try {
      const { data } = await api.get(`/orders/${id}/staff-state`);
      if (data.status !== "preparing") {
        setConfirmEarlyReady(null);
        toast.error("Status porudžbine je već promenjen.");
        reconcile();
        return;
      }
    } catch {
      setConfirmEarlyReady(null);
      toast.error("Nije moguće proveriti status porudžbine.");
      reconcile();
      return;
    }
    const fresh = orders.find((x) => x.id === id);
    await performTransition(fresh || { id, status: "preparing" }, "ready");
    setConfirmEarlyReady(null);
  };

  const submitTime = async (minutes) => {
    try { await api.post(`/orders/${dialog.id}/change-time`, { additional_minutes: minutes }); setDialog(null); toast.success("Novo vreme poslato kupcu"); }
    catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Greška");
      // A proposal already exists (another device won): the dialog is stale — close and resync.
      if (d?.code === "PICKUP_CHANGE_ALREADY_PENDING") { setDialog(null); reconcile(); return; }
      throw e;
    }
  };

  // Groups come from the single canonical helper (boardGroups.js): active preparation orders are
  // split by canonical order_source, NEW/time_change_pending stay in NOVE, READY in SPREMNO.
  const byStatus = useMemo(() => {
    const map = { new: [], ponesi: [], lokal: [], other: [], ready: [] };
    for (const o of orders) {
      const g = boardGroup(o);
      if (g) map[g].push(o);
    }
    // Sorting: NOVE oldest first (never forget a waiting order); active groups by urgency
    // (overdue first); SPREMNE oldest-ready first. Stable — no reordering on realtime ticks.
    map.new.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const byDeadline = (a, b) => {
      const da = orderDeadlineMs(a), db2 = orderDeadlineMs(b);
      if (da == null) return 1;
      if (db2 == null) return -1;
      return da - db2;
    };
    map.ponesi.sort(byDeadline);
    map.lokal.sort(byDeadline);
    map.other.sort(byDeadline);
    map.ready.sort((a, b) => new Date(a.ready_at || a.updated_at || a.created_at) - new Date(b.ready_at || b.updated_at || b.created_at));
    return map;
  }, [orders]);

  const counts = { new: byStatus.new.length, ponesi: byStatus.ponesi.length, lokal: byStatus.lokal.length, other: byStatus.other.length };
  // VISTA / LOKAL exists only with the canonical paid entitlement (GET /restaurant/{rid}/features).
  // While it is unresolved we do not flash the column; already-active Vista orders always keep it.
  const vistaEntitled = features?.VISTA === true;
  const tabs = tabGroups(counts, vistaEntitled);
  const columns = desktopGroups(counts, vistaEntitled);
  const ponesiHidden = counts.new > 0 && counts.ponesi > 0;
  useEffect(() => { if (!ponesiHidden) setPeekPonesi(false); }, [ponesiHidden]);
  // Keep the narrow-layout tab valid: never leave the user on a collapsed group.
  useEffect(() => {
    if (tabs.includes(tab)) return;
    setTab(counts.ponesi > 0 ? "ponesi" : counts.lokal > 0 ? "lokal" : tabs[0] || "ponesi");
    // eslint-disable-next-line
  }, [tabs.join(","), tab]);
  // A reminder firing in a hidden tab must still be noticeable.
  const accentGroups = new Set(Object.keys(accents).map((id) => boardGroup(orders.find((o) => o.id === id) || {})));

  const detailOrder = detail ? orders.find((o) => o.id === detail) : null;

  const renderCards = (key) => (
    <div className="space-y-3">
      {byStatus[key].map((o) => <OrderCard key={o.id} o={o} onAction={action} serverNow={nowTick + offsetRef.current} accent={accents[o.id]} onReward={(id, rec) => applyPatch(id, { new_customer_reward: rec })} />)}
      {byStatus[key].length === 0 && <p className="text-xs text-slate-300 py-2 text-center">Nema porudžbina</p>}
    </div>
  );

  return (
    <div>
      {/* Top operational toolbar: restaurant status · order acceptance · customer pickup time */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h1 className="font-display font-black text-2xl mr-auto">Porudžbine</h1>
        <OpsSelect testid="ops-status" value={tempClosed ? "closed" : "open"}
          label={tempClosed ? "PRIVREMENO ZATVORENO" : "OTVORENO"}
          className={tempClosed ? "bg-slate-800 text-white" : "bg-brand-green/10 text-brand-green"}
          options={[["open", "Otvoreno"], ["closed", "Privremeno zatvoreno"]]}
          onPick={(v) => setOps({ temp_closed: v === "closed" },
            v === "closed" ? "Da li želite privremeno da zatvorite restoran na Ponesi?" : null)} />
        <OpsSelect testid="ops-accepting" value={accepting ? "yes" : "no"}
          label={accepting ? "PRIMA PORUDŽBINE" : "NE PRIMA PORUDŽBINE"}
          className={accepting ? "bg-brand-green/10 text-brand-green" : "bg-brand-red hover:bg-brand-redHover text-white"}
          options={[["yes", "Prima porudžbine"], ["no", "Ne prima porudžbine"]]}
          onPick={(v) => setOps({ accepting_orders: v === "yes" },
            v === "no" ? "Da li želite da zaustavite prijem novih porudžbina?" : null)} />
        <OpsSelect testid="ops-time" value={range} icon={Clock} label={RANGE_LABELS[range] || "Vreme"}
          className="bg-slate-100 text-slate-700"
          options={Object.entries(RANGE_LABELS)}
          onPick={(v) => setOps({ normal_pickup_range: v }, null)} />
        <button data-testid="enable-notify" onClick={async () => { if (await restoreAudio()) playNewOrderBell(); const ok = await enablePush(); ok ? toast.success("Obaveštenja uključena") : toast.message("Obaveštenja nisu dozvoljena u pregledaču"); }}
          className="p-2 rounded-lg bg-slate-100" title="Uključi zvuk i obaveštenja"><Bell size={16} /></button>
        <button data-testid="refresh-orders" onClick={reconcile} className="p-2 rounded-lg bg-slate-100" title="Osveži"><RefreshCw size={16} /></button>
      </div>

      <ShiftControl rid={rid} variant="panel" shiftState={shiftState} canViewHistory={canViewShiftHistory}
        canViewFinancials={canViewShiftHistory} />

      {!audioUnlocked && (
        <div data-testid="audio-unlock-banner" className="mb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-brand-red text-white rounded-xl px-4 py-3">
          <div>
            <p className="font-black flex items-center gap-2"><Volume2 size={18} /> Uključite zvučna obaveštenja</p>
            <p className="text-sm font-semibold opacity-90">Zbog pravila uređaja potrebno je jednom dodirnuti ekran da bi zvuk novih porudžbina radio.</p>
          </div>
          <button data-testid="activate-sound-btn"
            onClick={async () => { if (await restoreAudio()) playNewOrderBell(); }}
            className="bg-white text-brand-red font-black text-sm px-4 min-h-[48px] rounded-lg shrink-0">UKLJUČI ZVUK</button>
        </div>
      )}

      {tempClosed && (
        <div data-testid="closed-banner-panel" className="mb-3 flex items-center gap-2 bg-slate-800 text-white rounded-xl px-4 py-2 text-sm font-semibold">
          Restoran je privremeno zatvoren na Ponesi
        </div>
      )}

      {/* Narrow screens: one focused active group at a time; SPREMNO stays a persistent strip. */}
      <div className="lg:hidden">
        <div className="flex gap-2 mb-3">
          {tabs.map((key) => (
            <button key={key} data-testid={`tab-${key}`} onClick={() => setTab(key)}
              className={`flex-1 min-w-0 rounded-xl py-2.5 text-xs font-black flex items-center justify-center gap-1.5 transition-colors ${tab === key ? "bg-brand-red hover:bg-brand-redHover text-white" : "bg-white text-slate-600"} ${tab !== key && accentGroups.has(key) ? "ring-2 ring-brand-orange" : ""}`}>
              <span className="truncate">{BOARD_GROUPS[key].title}</span>
              <span className={`text-xs rounded-full px-1.5 shrink-0 ${tab === key ? "bg-white/25" : "bg-slate-100"}`}>{counts[key]}</span>
            </button>
          ))}
        </div>
        {renderCards(tab)}
        <ReadyStrip orders={byStatus.ready} onOpen={(o) => setDetail(o.id)} onComplete={(o) => action(o, "completed")} className="mt-4 bg-slate-50 rounded-2xl p-3" />
      </div>

      {/* Desktop / landscape tablet: NOVE takes over the PONESI slot while new orders wait. */}
      <div className="hidden lg:flex gap-3 items-start">
        {columns.map((key) => (
          <div key={key} data-testid={`column-${key}`}
            style={{ flexGrow: groupFlex(key, counts.other > 0), flexBasis: 0 }}
            className="min-w-[12rem] xl:min-w-[14rem] bg-slate-50 rounded-2xl p-3 flex flex-col board-pane transition-[flex-grow] duration-300 ease-out">
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-2 min-w-0"><span className={`w-2.5 h-2.5 rounded-full shrink-0 ${BOARD_GROUPS[key].tint}`} /><h2 className="font-display font-black text-sm tracking-wide truncate">{BOARD_GROUPS[key].title}</h2></div>
              <span className="text-sm font-black bg-white rounded-full px-2.5 py-0.5 shrink-0">{counts[key]}</span>
            </div>
            {/* Hidden-PONESI summary: informational chip, opens a temporary inspection drawer. */}
            {key === "new" && ponesiHidden && (
              <button data-testid="ponesi-hidden-chip" onClick={() => setPeekPonesi(true)}
                aria-label={`${counts.ponesi} Ponesi porudžbine u pripremi`}
                className={`mb-3 w-full text-[11px] font-black rounded-full px-3 py-1.5 bg-white text-slate-600 border transition-colors ${
                  accentGroups.has("ponesi") ? "border-brand-orange ring-2 ring-brand-orange/30"
                  : byStatus.ponesi.some((o) => { const dl = orderDeadlineMs(o); return dl != null && dl - (nowTick + offsetRef.current) <= 0; }) ? "border-brand-red text-brand-red"
                  : "border-slate-200"}`}>
                PONESI <span className="xl:hidden">{counts.ponesi}</span><span className="hidden xl:inline">{counts.ponesi} u pripremi</span>
              </button>
            )}
            <div className="board-pane-scroll min-h-0">{renderCards(key)}</div>
          </div>
        ))}
        <ReadyStrip orders={byStatus.ready} onOpen={(o) => setDetail(o.id)} onComplete={(o) => action(o, "completed")} className="w-24 xl:w-32 shrink-0 bg-slate-50 rounded-2xl p-2 flex flex-col board-pane" scrollClassName="board-pane-scroll min-h-0" />
      </div>

      {/* Temporary PONESI inspection drawer — same OrderCard, no extra column, NOVE stays primary. */}
      {peekPonesi && (
        <div className="hidden lg:block fixed inset-0 z-40" data-testid="ponesi-peek">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setPeekPonesi(false)} />
          <div className="absolute right-0 top-0 h-full w-[26rem] max-w-[90vw] bg-white shadow-2xl p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-black text-lg">PONESI U PRIPREMI <span className="text-slate-400">{counts.ponesi}</span></h2>
              <button data-testid="ponesi-peek-close" onClick={() => setPeekPonesi(false)} className="text-slate-400 hover:text-slate-800"><X size={20} /></button>
            </div>
            {renderCards("ponesi")}
          </div>
        </div>
      )}

      {detailOrder && detailOrder.status === "ready" && (
        <OrderDetailModal o={detailOrder} onClose={() => setDetail(null)} onComplete={(o) => action(o, "completed")} />
      )}

      {confirmReject && (
        <ConfirmRejectDialog order={confirmReject}
          onCancel={() => setConfirmReject(null)}
          onConfirm={async (o) => { await performTransition(o, "reject"); setConfirmReject(null); }} />
      )}

      {confirmEarlyReady && (
        <ConfirmEarlyReadyDialog order={confirmEarlyReady} remainingMs={confirmEarlyReady.remainingMs}
          onCancel={() => setConfirmEarlyReady(null)}
          onConfirm={confirmEarlyReadyNow} />
      )}

      {confirmCancel && (
        <ConfirmCancelDialog order={confirmCancel} onCancel={() => setConfirmCancel(null)}
          onConfirm={cancelOrderNow} />
      )}

      {dialog && <TimeChangeDialog order={dialog} onClose={() => setDialog(null)} onSubmit={submitTime} />}    </div>
  );
}
